import type { FC } from 'react';
import { Text } from '@launchpad-ui/components';
import moment from 'moment';

import { vars } from '../../../../../css/vars';
import type { BaseContentProps } from '../Content';

const FALLBACK_EMPTY_TIME = '---------- --:-- --';
const FALLBACK_EMPTY_TIME_WITH_SECONDS = '---------- --:--:-- --';

const TIME_FORMAT = 'YYYY-MM-DD hh:mm A';
const TIME_FORMAT_WITH_SECONDS = 'YYYY-MM-DD hh:mm:ss A';

export interface TimeContentProps extends BaseContentProps {
  /**
   * The date/time value to display.
   * Can be a Date object, timestamp (number), or ISO string.
   * If not provided, displays a placeholder matching the format shape.
   */
  value?: Date | number | string;

  /**
   * Optional tooltip text to show on hover.
   * If not provided, shows the full ISO timestamp.
   */
  tooltip?: string;

  /**
   * Text style variant
   * @default 'default'
   */
  variant?: 'default' | 'small' | 'monotype';

  /**
   * When true, includes seconds in the formatted output.
   * Default: "2026-12-10 03:12 PM"
   * With seconds: "2026-12-10 03:12:03 PM"
   * @default false
   */
  showSeconds?: boolean;
}

/**
 * Time content component for use inside Cell.
 * Displays formatted date/time values with optional tooltip.
 *
 * Format: 2026-12-10 03:12 PM (or 2026-12-10 03:12:03 PM with showSeconds)
 * Empty: ---------- --:-- -- (or ---------- --:--:-- -- with showSeconds)
 *
 * @example
 * ```tsx
 * // Regular timestamp
 * <Cell kind="regular" size="default">
 *   <TimeContent value={new Date()} />
 * </Cell>
 *
 * // With seconds (for Logs)
 * <Cell kind="regular" size="default">
 *   <TimeContent value={new Date()} showSeconds />
 * </Cell>
 *
 * // Monotype timestamp
 * <Cell kind="regular" size="default">
 *   <TimeContent value="2024-01-01T12:00:00Z" variant="monotype" />
 * </Cell>
 *
 * // Empty state
 * <Cell kind="regular" size="default">
 *   <TimeContent />
 * </Cell>
 * ```
 */
export const TimeContent: FC<TimeContentProps> = ({
  value,
  tooltip,
  className,
  variant = 'default',
  showSeconds = false,
}) => {
  const getTextProps = () => {
    switch (variant) {
      case 'small':
        return { size: 'small' as const };
      case 'monotype':
        return { size: 'medium' as const };
      case 'default':
      default:
        return { size: 'medium' as const };
    }
  };

  const { size } = getTextProps();
  const dt = value ? (typeof value === 'string' ? moment(value, moment.ISO_8601, true) : moment(value)) : null;
  const format = showSeconds ? TIME_FORMAT_WITH_SECONDS : TIME_FORMAT;
  const fallback = showSeconds ? FALLBACK_EMPTY_TIME_WITH_SECONDS : FALLBACK_EMPTY_TIME;
  const displayValue = dt?.isValid() ? dt.format(format) : fallback;
  const isEmpty = dt == null || !dt.isValid();

  const defaultTooltip = dt?.isValid() ? dt.toISOString() : undefined;

  return (
    <Text
      size={size}
      elementType="span"
      className={className}
      maxLines={1}
      style={{
        fontFamily: variant === 'monotype' ? 'var(--lp-font-family-monospace)' : undefined,
        whiteSpace: 'nowrap',
        // Empty state color
        color: isEmpty ? vars.theme.interactive.fill.secondary.content.onDisabled : undefined,
      }}
      title={tooltip || defaultTooltip}
    >
      {displayValue}
    </Text>
  );
};
